import { useFormContext } from 'react-hook-form';

import { Box, useTheme } from '@mui/material';
import { EditCTAButtonsProps, ScorecardToSubmit } from '../../../../types/scorecardTypes';
import { PrimaryButton } from 'sharedComponents/Buttons';
import { primaryButtonOverrideSx } from 'sharedComponents/Buttons/styles';
import { GreySecondaryButton } from 'sharedComponents/Buttons/GreySecondaryButton';
import { ATSConnectHiringRequestCreateFeedbackTestIds } from 'data-testids/ATS';

export const EditCTAButtons: React.FC<EditCTAButtonsProps> = ({
  onCancel,
  isLoading,
  isComplete,
  onSave,
  onSaveAsDraft,
  isScorecard,
}) => {
  const theme = useTheme();
  const {
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    reset,
    trigger,
    clearErrors,
  } = useFormContext<ScorecardToSubmit>();

  const cumulativeScore = watch('cumulative_score');
  const maxScore = watch('max_cumulative_score');

  const submitShouldBeDisabled =
    Object.keys(errors).length > 0 || cumulativeScore > maxScore || isLoading;

  const handleSaveClick = async (isComplete: boolean) => {
    if (isLoading) return;

    if (isScorecard) {
      setValue('scorecard_complete', isComplete);
    } else {
      setValue('is_complete', isComplete);
    }

    if (isComplete) {
      const isValid = await trigger();
      const formData = watch();

      if (isValid) {
        onSave(formData);
      }
    } else {
      clearErrors();
      const formData = watch();
      onSaveAsDraft(formData);
    }
  };

  return (
    <Box
      sx={{
        paddingTop: theme.spacing(4),
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'row',
      }}
    >
      <GreySecondaryButton
        onClick={() => {
          reset();
          onCancel();
        }}
        dataTestId={ATSConnectHiringRequestCreateFeedbackTestIds.EDIT_CANCEL_BUTTON}
        sx={{
          width: isComplete ? '50%' : '25%',
          marginRight: isComplete ? theme.spacing(1) : 'inherit',
        }}
      >
        Cancel
      </GreySecondaryButton>
      <Box sx={{ width: '50%', display: 'flex' }}>
        {!isComplete && (
          <GreySecondaryButton
            onClick={() => handleSaveClick(false)}
            dataTestId={ATSConnectHiringRequestCreateFeedbackTestIds.EDIT_CANCEL_BUTTON}
            sx={{
              width: '50%',
              marginRight: theme.spacing(1),
            }}
          >
            Save as draft
          </GreySecondaryButton>
        )}
        <PrimaryButton
          onClick={() => handleSubmit(() => handleSaveClick(true))()}
          disabled={submitShouldBeDisabled}
          sx={{ ...primaryButtonOverrideSx(theme), width: isComplete ? '100%' : '50%' }}
        >
          {isComplete ? 'Save Changes' : 'Submit'}
        </PrimaryButton>
      </Box>
    </Box>
  );
};
